import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  Legend,
  Label,
  ErrorBar
} from 'recharts';
import domtoimage from 'dom-to-image';
import fileDownload from 'js-file-download';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { FaCamera } from 'react-icons/fa';

import { getNumberInFormat, getXAxisTicks } from '../utils';
import CustomTooltip from './customTooltip';

const Chart = ({ 
  data, 
  title,
  description='No definition available',
  unit=' ',
  titleAlignment, 
  width, 
  height, 
  boldLabel, 
  fileName, 
  showCloseBtn, 
  onClose
}) => {
  const marginLeft = 275;
  const marginTop = 50;

  const margin = {
    top: titleAlignment === 'top' ? marginTop : 5,
    left: titleAlignment === 'left' ? marginLeft : 0,
    bottom: 0,
    right: 0
  };

  const labelClass = classNames('chart-label', {
    'font-weight-bold': boldLabel
  });

  const { t } = useTranslation();

  const xAxisTicks = getXAxisTicks(data);

  const chartRef = React.useRef();

  const yAxisTickFormat = (num) => {
    return getNumberInFormat(num);
  };

  const handleDownload = async () => {
    const blob = await domtoimage.toBlob(chartRef.current, { bgcolor: '#fff' });
    fileDownload(blob, `${fileName}.png`);
  };

  const renderLegendText = () => {
    return <span style={{ position: "absolute", right: 0, top: "-15px" }}>time [s]</span>;
  };

  const renderLabelLeft = () => {
    return (
      <g className={"label-parent"}>
        <text offset="5" x="65" y="45" className="recharts-text chart-label" textAnchor="start">
          <tspan x="65" dy="0.355em">{`${title} [${unit}]`}</tspan>
        </text>
        <foreignObject x="65" y="52" width="500" height="100" style={{ "visibility": "hidden" }} className={"label-definition"}>
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ "border": "1px black solid", "padding": "5px", "backgroundColor": "#f3f6f6",
            "overflowY": "auto", "maxHeight": "88px" }}>{description}</div>
        </foreignObject>
      </g>
    );
  };

  const renderLabelUp = () => {
    return (
      <g className={"label-parent"}>
        <text offset="5" x="289.5" y="20" className="recharts-text chart-label" textAnchor="middle">
          <tspan x="289.5" dy="0em">{`${title} [${unit}]`}</tspan>
        </text>
        <foreignObject x="0" y="23" width="500" height="100" style={{ "visibility": "hidden" }} className={"label-definition"}>
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ "border": "1px black solid", "padding": "5px", "backgroundColor": "#f3f6f6",
            "overflowY": "auto", "maxHeight": "88px" }}>{description}</div>
        </foreignObject>
      </g>
    );
  };


  return (
    <div className='chart-wrapper'>
      <button 
        className='download-chart-btn' 
        onClick={handleDownload} 
        title={t('download_chart_button_label')}
      >
        <FaCamera className='camera-icon' />
      </button>
      {showCloseBtn ? (
        <Button 
          color='link' 
          className='btn-bg-image remove-chart-btn btn-remove' 
          onClick={onClose}
          title={t('remove_chart_button_label')}
        >
        </Button>
      ) : (
        null
      )}
      <div ref={chartRef}>
        <LineChart width={width} height={height} data={data} margin={margin}>
          { data && data.length && <YAxis
            type='number' 
            dataKey={(v) => Number(v.value)}
            tickFormatter={yAxisTickFormat} 
            interval={0}
            tick={{ fontSize: 12 }}
            tickMargin={5}
            padding={{ top: 5, bottom: 5 }}
          /> }
          <CartesianGrid strokeDasharray='3 3' />
          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegendText} />
          { 
            data && data.length && <Line
              type='linear' 
              dataKey='value'
              stroke='#3071b3'
              strokeWidth={2.0}
              isAnimationActive={false}
            > 
              <ErrorBar
                dataKey="error"
                stroke="#3071b3"
                strokeWidth={1.0} 
                direction="y"
                isAnimationActive={false}
              />
            </Line>
          }
          <XAxis
            type='number'
            dataKey='time'
            tick={{ fontSize: 12 }}
            ticks={xAxisTicks}
            padding={{ left: 5, right: 5 }}
            domain={[ 'dataMin', 'dataMax' ]}
          >
            {titleAlignment === 'top' ? (
              <Label className={labelClass} data={description} position='insideBottom' dy={-height + marginTop/2} content={renderLabelUp} />
            ) : (
              <Label className={labelClass} data={description} position='insideLeft' dy={-height/2} dx={-marginLeft} content={renderLabelLeft} />
            )}
          </XAxis>
        </LineChart>
      </div>
    </div>
  );
};

Chart.defaultProps = {
  titleAlignment: 'left',
  width: 720,
  height: 140,
  boldLabel: false,
  showCloseBtn: false
};

Chart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.number.isRequired,
    value: PropTypes.number,
    error: PropTypes.arrayOf(PropTypes.number)
  })),
  title: PropTypes.string.isRequired,
  description:PropTypes.string,
  unit: PropTypes.string,
  titleAlignment: PropTypes.oneOf([ 'left', 'top' ]),
  width: PropTypes.number,
  height: PropTypes.number,
  boldLabel: PropTypes.bool,
  fileName: PropTypes.string.isRequired,
  showCloseBtn: PropTypes.bool,
  onClose: PropTypes.func,
};

export default memo(Chart);
